import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import { translate as t } from 'hooks/useTranslations';
import { neutral } from '_lex/_foundations/phxColors';
import { useLexAiChatFlow } from '../_data_sources/useLexAiChatFlow';
import { CHAT_MAX_MESSAGE_LENGTH } from '../_types';
import type { ChatMessage } from '../_types';

export default function Chat() {
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const {
    messages,
    partialInbound,
    status,
    connectionError,
    send,
    clear,
    stop,
    canSend,
    validationError,
  } = useLexAiChatFlow(true);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, partialInbound]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || !canSend) return;
    send(input);
    setInput('');
  };

  const isStreaming = status === 'streaming' || status === 'sending';

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateRows: '1fr auto auto',
        height: '100%',
        minHeight: 0,
      }}
    >
      <Box
        sx={{
          overflow: 'auto',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {connectionError && (
          <Typography color='error' variant='body2'>
            {t('lexAi.connectionError')}
          </Typography>
        )}
        {validationError && (
          <Typography color='error' variant='body2'>
            {validationError}
          </Typography>
        )}
        {status === 'connecting' && (
          <Typography color='text.secondary' variant='body2'>
            {t('lexAi.connecting')}
          </Typography>
        )}
        {messages.length === 0 && !partialInbound && !connectionError && status !== 'connecting' && (
          <Typography color='text.secondary' variant='body2'>
            {t('lexAi.placeholder')}
          </Typography>
        )}
        {messages.map((msg: ChatMessage) => (
          <Box
            key={msg.id}
            sx={{
              alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
              maxWidth: '85%',
              p: 2,
              borderRadius: 2,
              bgcolor: msg.role === 'user' ? neutral[100] : neutral[50],
              border: '1px solid',
              borderColor: neutral[200],
            }}
          >
            <Typography variant='body1' sx={{ whiteSpace: 'pre-wrap' }}>
              {msg.text}
            </Typography>
          </Box>
        ))}
        {partialInbound && (
          <Box
            sx={{
              alignSelf: 'flex-start',
              maxWidth: '85%',
              p: 2,
              borderRadius: 2,
              bgcolor: neutral[50],
              border: '1px solid',
              borderColor: neutral[200],
            }}
          >
            <Typography variant='body1' sx={{ whiteSpace: 'pre-wrap' }}>
              {partialInbound}
            </Typography>
          </Box>
        )}
        <div ref={messagesEndRef} />
      </Box>

      <Stack direction='row' spacing={1} sx={{ p: 1, flexShrink: 0 }} justifyContent='flex-end'>
        {isStreaming && (
          <Button variant='outlined' size='small' onClick={stop}>
            {t('lexAi.stop')}
          </Button>
        )}
        <Button variant='outlined' size='small' onClick={clear}>
          {t('lexAi.newChat')}
        </Button>
      </Stack>

      <Box component='form' onSubmit={handleSubmit} sx={{ p: 2, flexShrink: 0 }}>
        <Stack direction='row' spacing={1} alignItems='flex-end'>
          <TextField
            fullWidth
            multiline
            maxRows={4}
            placeholder={t('lexAi.placeholder')}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            disabled={!canSend}
            inputProps={{ maxLength: CHAT_MAX_MESSAGE_LENGTH }}
            size='small'
          />
          <Button type='submit' variant='contained' disabled={!canSend || !input.trim()}>
            {t('lexAi.send')}
          </Button>
        </Stack>
        {input.length > CHAT_MAX_MESSAGE_LENGTH * 0.9 && (
          <Typography variant='caption' color='text.secondary' sx={{ mt: 0.5 }}>
            {input.length} / {CHAT_MAX_MESSAGE_LENGTH}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
